
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from '@/components/ui/use-toast';
import { Mail, CheckCircle } from 'lucide-react';
import PasswordInput from '@/components/auth/PasswordInput';
import NauticLogo from '@/components/ui/NauticLogo';
import LanguageSwitcher from '@/components/layout/LanguageSwitcher';
import PasswordStrengthIndicator, { validatePassword } from '@/components/auth/PasswordStrengthIndicator';
import SimpleCaptcha from '@/components/auth/SimpleCaptcha';

const AuthPage = () => {
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [showEmailConfirmation, setShowEmailConfirmation] = useState(false);
  const [captchaValid, setCaptchaValid] = useState(false);
  const { signUp, signIn, user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation('auth');

  // Redirect if already logged in
  React.useEffect(() => {
    if (user) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      toast({
        title: t('errors.title'),
        description: t('errors.fillAll'),
        variant: "destructive",
      });
      return;
    }

    if (isSignUp && !validatePassword(password)) {
      toast({
        title: t('errors.title'),
        description: t('errors.weakPassword'),
        variant: "destructive",
      });
      return;
    }

    if (isSignUp && password !== confirmPassword) {
      toast({
        title: t('errors.title'),
        description: t('errors.passwordMismatch'),
        variant: "destructive",
      });
      return;
    }

    if (isSignUp && !captchaValid) {
      toast({
        title: t('errors.title'),
        description: t('errors.captcha'),
        variant: "destructive",
      });
      return;
    }
    
    try {
      const { error } = isSignUp 
        ? await signUp(email, password)
        : await signIn(email, password);

      if (error) {
        let errorMessage = t('errors.generic');
        
        if (error.message.includes('Email not confirmed')) {
          errorMessage = t('errors.emailNotConfirmed');
        } else if (error.message.includes('already registered')) {
          errorMessage = t('errors.alreadyRegistered');
        } else if (error.message.includes('Invalid login credentials')) {
          errorMessage = t('errors.invalidCredentials');
        } else if (error.message.includes('signup')) {
          errorMessage = t('errors.signupError');
        }
        
        toast({
          title: t('errors.title'),
          description: errorMessage,
          variant: "destructive",
        });
      } else {
        if (isSignUp) {
          setShowEmailConfirmation(true);
          setEmail('');
          setPassword('');
          setConfirmPassword('');
        } else {
          toast({
            title: t('success.loginTitle'),
            description: t('success.loginDescription'),
          });
          navigate('/dashboard');
        }
      }
    } catch (err) {
      toast({
        title: t('errors.title'),
        description: t('errors.unexpected'),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetEmail) {
      toast({ title: t('errors.title'), description: t('errors.resetEmailRequired'), variant: "destructive" });
      return;
    }
    setResetLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) {
        toast({ title: t('errors.title'), description: t('errors.resetSendError'), variant: "destructive" });
      } else {
        toast({
          title: t('success.resetTitle'),
          description: t('success.resetDescription'),
        });
        setShowForgotPassword(false);
        setResetEmail('');
      }
    } catch {
      toast({ title: t('errors.title'), description: t('errors.unexpected'), variant: "destructive" });
    } finally {
      setResetLoading(false);
    }
  };

  if (showEmailConfirmation) {
    return (
      <div className="min-h-screen bg-nautic flex items-center justify-center p-4">
        <Card className="w-full max-w-md border border-nautic-gold/30 shadow-2xl bg-white">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="rounded-full bg-nautic-gold/15 p-4">
                <Mail className="h-10 w-10 text-nautic-gold" />
              </div>
            </div>
            <CardTitle className="text-2xl font-bold text-nautic">
              {t('confirmation.title')}
            </CardTitle>
            <CardDescription className="text-base mt-2 text-muted-foreground">
              {t('confirmation.description')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-start gap-2 p-3 rounded-md bg-nautic-gold/10 border border-nautic-gold/20 text-sm text-nautic">
              <CheckCircle className="h-5 w-5 text-nautic-gold mt-0.5 shrink-0" />
              <span>{t('confirmation.spam')}</span>
            </div>
            <Button
              className="w-full bg-nautic hover:bg-nautic-light text-white"
              onClick={() => {
                setShowEmailConfirmation(false);
                setIsSignUp(false);
              }}
            >
              {t('confirmation.back')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-nautic flex items-center justify-center p-4">
      <Card className="w-full max-w-md border border-nautic-gold/30 shadow-2xl bg-white">
        <CardHeader className="text-center">
          <div className="flex justify-end">
            <LanguageSwitcher />
          </div>
          <div className="flex justify-center mb-2">
            <NauticLogo withText textSize="lg" className="text-nautic-gold" />
          </div>
          <CardDescription className="text-muted-foreground">
            {isSignUp ? t('subtitleSignup') : t('subtitleLogin')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">{t('email')}</Label>
              <Input
                id="email"
                type="email"
                placeholder={t('emailPlaceholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password">{t('password')}</Label>
              <PasswordInput
                id="password"
                placeholder={t('passwordPlaceholder')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>

            {isSignUp && (
              <PasswordStrengthIndicator password={password} />
            )}

            {isSignUp && (
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">{t('confirmPassword')}</Label>
                <PasswordInput
                  id="confirmPassword"
                  placeholder={t('confirmPasswordPlaceholder')}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
              </div>
            )}

            {isSignUp && (
              <SimpleCaptcha onValidChange={setCaptchaValid} />
            )}

            <Button 
              type="submit" 
              className="w-full bg-nautic hover:bg-nautic-light text-white"
              disabled={loading}
            >
              {loading ? t('loading') : (isSignUp ? t('signUp') : t('signIn'))}
            </Button>
          </form>

          <div className="mt-4 text-center space-y-2">
            <button
              type="button"
              onClick={() => setIsSignUp(!isSignUp)}
              className="text-nautic hover:underline text-sm"
            >
              {isSignUp ? t('switchToLogin') : t('switchToSignup')}
            </button>

            {!isSignUp && (
              <div>
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(!showForgotPassword)}
                  className="text-muted-foreground hover:text-nautic hover:underline text-xs"
                >
                  {t('forgotPassword')}
                </button>

                {showForgotPassword && (
                  <form onSubmit={handleResetPassword} className="mt-3 space-y-3 p-3 border rounded-md bg-muted/30">
                    <p
                      className="text-xs text-muted-foreground"
                      dangerouslySetInnerHTML={{ __html: t('resetHint') }}
                    />
                    <Input
                      type="email"
                      placeholder={t('resetPlaceholder')}
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      required
                    />
                    <Button
                      type="submit"
                      variant="secondary"
                      className="w-full"
                      disabled={resetLoading}
                    >
                      {resetLoading ? t('sending') : t('send')}
                    </Button>
                  </form>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AuthPage;
